<template>
  <div v-if="media && media.length > 0" class="media-section" dir="ltr" @touchstart.stop="handleTouchStart">
    <div class="media-container" :class="`layout-${layoutType}`" :style="containerStyle">

      <!-- Single Media Layout -->
      <div v-if="layoutType === 'single'" class="single-layout">
        <div class="media-item" @click="openGallery(0)">
          <optimized-media-item
            :media="media[0]"
            :lazy="false"
            quality="medium"
            show-play-button
          ></optimized-media-item>
        </div>
      </div>

      <!-- Dual Media Layout -->
      <div v-else-if="layoutType === 'dual'" class="dual-layout">
        <div
          v-for="(item, index) in media.slice(0, 2)"
          :key="`${postId}-media-${item.id || index}`"
          class="media-item"
          @click="openGallery(index)"
        >
          <optimized-media-item
            :media="item"
            :lazy="index > 0"
            quality="medium"
            show-play-button
          ></optimized-media-item>
        </div>
      </div>

      <!-- Grid Layout (3+ items) - RTL: grid LEFT, hero RIGHT -->
      <div v-else class="grid-layout">
        <!-- Secondary Grid (LEFT) - shows items 1,2 -->
        <div class="secondary-grid">
          <div
            v-for="(item, index) in media.slice(1, 3)"
            :key="`${postId}-media-${item.id || (index + 1)}`"
            class="media-item secondary-item"
            :class="{ 'has-overlay': index === 1 && media.length > 3 }"
            @click="openGallery(index + 1)"
          >
            <optimized-media-item
              :media="item"
              :lazy="index > 0"
              quality="medium"
              show-play-button
            ></optimized-media-item>
            <!-- Count overlay on last grid item -->
            <div v-if="index === 1 && media.length > 3" class="count-overlay">
              <span class="count-text">+{{ media.length - 3 }}</span>
            </div>
          </div>
        </div>

        <!-- Primary Hero Image (RIGHT) -->
        <div class="media-item primary-item" @click="openGallery(0)">
          <optimized-media-item
            :media="media[0]"
            :lazy="false"
            quality="medium"
            show-play-button
          ></optimized-media-item>
        </div>
      </div>

      <!-- AI Enhance Button (bottom-LEFT for RTL) -->
      <!--      <f7-button
        class="ai-enhance-button"
        :class="{ 'ai-enhanced': aiEnhanced, 'ai-loading': aiLoading }"
        round
        fill
        :aria-label="aiEnhanced ? 'AI Enhanced' : 'Enhance with AI'"
        :disabled="aiLoading"
        @click.stop="handleAiClick"
      >
        <f7-icon f7="sparkles" :class="{ 'icon-spin': aiLoading }"></f7-icon>
        <span v-if="aiEnhanced" class="ai-badge" />
      </f7-button>-->
    </div>

    <!-- Photo Browser -->
    <f7-photo-browser
      v-if="showPhotoBrowser"
      ref="photoBrowser"
      :photos="photoBrowserPhotos"
      :params="photoBrowserConfig"
    ></f7-photo-browser>
  </div>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, onUnmounted, ref, shallowRef } from 'vue';
import OptimizedMediaItem from './MediaItem.vue';

const props = defineProps({
  media: {
    type: Array,
    required: true,
    validator: (media) =>
      Array.isArray(media) &&
        media.length > 0 &&
        media.every(
          (item) =>
            item &&
                typeof item === 'object' &&
                ['image', 'video'].includes(item.mediaType) &&
                typeof item.url === 'string' &&
                item.url.length > 0,
        ),
  },
  productName: {
    type: String,
    default: '',
  },
  postId: {
    type: [String, Number],
    required: true,
  },
  aiLoading: {
    type: Boolean,
    default: false,
  },
  aiEnhanced: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['ai-click']);

const showPhotoBrowser = ref(false);
const photoBrowser = ref(null);
const photoBrowserInstance = shallowRef(null);

const layoutType = computed(() => {
  const count = props.media.length;
  if (count === 1) return 'single';
  if (count === 2) return 'dual';
  return 'grid';
});

const screenSize = computed(() => {
  const width = window.innerWidth;
  if (width <= 360) return 'small-mobile';
  if (width <= 480) return 'mobile';
  return 'desktop';
});

const containerStyle = computed(() => {
  const heightMap = {
    'small-mobile': { default: '240px', max: '320px' },
    'mobile': {
      single: '280px',
      dual: '240px',
      grid: '280px',
      max: '400px',
    },
    'desktop': {
      single: '400px',
      dual: '300px',
      grid: '380px',
      max: '500px',
    },
  };

  const size = screenSize.value;
  const type = layoutType.value;

  let height, maxHeight;

  if (size === 'small-mobile') {
    height = heightMap[size].default;
    maxHeight = heightMap[size].max;
  } else {
    height = heightMap[size][type] || heightMap[size].grid;
    maxHeight = heightMap[size].max;
  }

  return { height, minHeight: height, maxHeight };
});

const photoBrowserPhotos = computed(() =>
  props.media.map((item, index) => {
    // Generate alt text from product name or media ID
    const altText = item.alt ||
          props.productName ||
          `صورة ${index + 1}`;

    return {
      url: item.urls?.original || item.url,
      caption: `${altText} (${index + 1}/${props.media.length})`,
      type: item.mediaType,
    };
  }),
);

const photoBrowserConfig = computed(() => ({
  type: 'standalone',
  toolbar: true,
  closeByBackdropClick: true,
  exposition: true,
  swiper: {
    zoom: {
      enabled: true,
      maxRatio: 3,
      minRatio: 1,
    },
    lazy: true,
    keyboard: { enabled: true },
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    },
  },
  on: {
    close: () => {
      showPhotoBrowser.value = false;
      photoBrowserInstance.value = null;
    },
  },
}));

const openGallery = (startIndex = 0) => {
  const validIndex = Math.max(0, Math.min(startIndex, props.media.length - 1));

  try {
    if (photoBrowserInstance.value) {
      photoBrowserInstance.value.destroy();
      photoBrowserInstance.value = null;
    }

    photoBrowserInstance.value = f7.photoBrowser.create({
      photos: photoBrowserPhotos.value,
      ...photoBrowserConfig.value,
    });

    photoBrowserInstance.value.open(validIndex);
  } catch (error) {
    console.error('Failed to open photo browser:', error);
    const mediaItem = props.media[validIndex];
    if (mediaItem?.url) {
      window.open(mediaItem.url, '_blank', 'noopener,noreferrer');
    }
  }
};
function handleTouchStart(event) {
  event.stopPropagation();
  // ... other logic
}
onUnmounted(() => {
  if (photoBrowserInstance.value) {
    photoBrowserInstance.value.destroy();
  }
});

defineExpose({ openGallery });
</script>

<style scoped>
.media-section {
  padding: 0 12px 12px;
}

.media-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background: var(--f7-color-gray-100);
  touch-action: pan-y pinch-zoom;
}

.media-item {
  position: relative;
  cursor: pointer;
  overflow: hidden;
  background: var(--f7-color-gray-200);
  transition: transform 0.2s ease;
}

.media-item:active {
  transform: scale(0.98);
}

.media-item :deep(.media-wrapper) {
  width: 100%;
  height: 100%;
}

/* Single layout */
.single-layout {
  width: 100%;
  height: 100%;
}

.single-layout .media-item {
  width: 100%;
  height: 100%;
  border-radius: 12px;
}

/* Dual layout */
.dual-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  height: 100%;
}

.dual-layout .media-item {
  height: 100%;
}

.dual-layout .media-item:first-child {
  border-radius: 12px 0 0 12px;
}

.dual-layout .media-item:last-child {
  border-radius: 0 12px 12px 0;
}

/* Grid layout - RTL optimized */
.grid-layout {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 4px;
  height: 100%;
}

.primary-item {
  height: 100%;
  border-radius: 0 12px 12px 0;
}

.secondary-grid {
  display: grid;
  grid-template-rows: 1fr 1fr;
  grid-template-columns: 1fr;
  gap: 4px;
}

.secondary-item {
  position: relative;
  height: 100%;
}

.secondary-grid .secondary-item:nth-child(1) {
  border-radius: 12px 0 0 0;
}

.secondary-grid .secondary-item:nth-child(2) {
  border-radius: 0 0 0 12px;
}

/* Count overlay */
.count-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(2px);
  z-index: 4;
}

.count-text {
  color: white;
  font-size: 20px;
  font-weight: 600;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}

/* AI Enhance Button */
.ai-enhance-button {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 10;
  width: 44px;
  height: 44px;
  min-width: 44px;
  min-height: 44px;
  padding: 0;
  border: 2px solid rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.ai-enhance-button :deep(.icon) {
  font-size: 20px;
  color: var(--f7-theme-color, #a855f7);
}

.ai-enhance-button:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(168, 85, 247, 0.3);
}

.ai-enhance-button:active {
  transform: scale(0.95);
}

.ai-enhanced {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  border-color: #9333ea;
}

.ai-enhanced :deep(.icon) {
  color: white;
}

.ai-loading {
  pointer-events: none;
  opacity: 0.8;
}

.icon-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.ai-badge {
  position: absolute;
  top: -6px;
  left: -6px;
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 8px;
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  line-height: 1;
  letter-spacing: 0.5px;
}

/* Mobile responsive */
@media (max-width: 480px) {
  .media-section {
    padding: 0 8px 8px;
  }

  .media-container {
    border-radius: 8px;
  }

  .single-layout .media-item {
    border-radius: 8px;
  }

  .dual-layout .media-item:first-child {
    border-radius: 8px 0 0 8px;
  }

  .dual-layout .media-item:last-child {
    border-radius: 0 8px 8px 0;
  }

  .primary-item {
    border-radius: 0 8px 8px 0;
  }

  .secondary-grid .secondary-item:nth-child(1) {
    border-radius: 8px 0 0 0;
  }

  .secondary-grid .secondary-item:nth-child(2) {
    border-radius: 0 0 0 8px;
  }

  .count-text {
    font-size: 16px;
  }

  .ai-enhance-button {
    width: 40px;
    height: 40px;
    min-width: 40px;
    min-height: 40px;
    bottom: 6px;
    left: 6px;
  }

  .ai-enhance-button :deep(.icon) {
    font-size: 18px;
  }

  .ai-badge {
    font-size: 9px;
    padding: 1px 4px;
    top: -4px;
    left: -4px;
  }
}

@media (max-width: 360px) {
  .grid-layout {
    grid-template-columns: 2fr 3fr;
  }

  .count-text {
    font-size: 14px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .media-item,
  .ai-enhance-button,
  .icon-spin {
    animation: none;
    transition: none;
  }
}

.media-item:focus-visible,
.ai-enhance-button:focus-visible {
  outline: 2px solid var(--f7-theme-color);
  outline-offset: 2px;
}
</style>
